import surfaceGLSL from "./utils/surface.glsl";

import normalsGLSL from "./utils/normals.glsl";

const fragmentShader = /* glsl */ `
precision highp float;

varying vec3 vNormal;
varying vec2 vUv;
varying vec3 vWorldPosition;


${surfaceGLSL}
${normalsGLSL}


/*
 * Lighting.
 */
uniform vec3 uLightDirection;
uniform vec3 uLightColor;

uniform float uLightIntensity;
uniform float uAmbientIntensity;


/*
 * Technical illustration.
 */
uniform vec3 uLineColor;

uniform float uSilhouetteWidth;
uniform float uDetailStrength;
uniform float uShadingStrength;


/*
 * ============================================================
 * SILHOUETTE
 * ============================================================
 */

float getSilhouette(
    vec3 normal
) {

    /*
     * Camera position is provided by
     * Three.js in world space.
     */
    vec3 viewDirection =
        normalize(
            cameraPosition -
            vWorldPosition
        );


    /*
     * N dot V approaches zero as the
     * surface turns perpendicular to
     * the viewer.
     */
    float facing =
        abs(
            dot(
                normal,
                viewDirection
            )
        );


    /*
     * Convert grazing angles into an
     * edge mask.
     */
    float width =
        max(
            uSilhouetteWidth,
            0.001
        );


    return 1.0 -
        smoothstep(
            0.0,
            width,
            facing
        );
}


/*
 * ============================================================
 * DETAIL EDGES
 * ============================================================
 */

float getDetailEdge(
    vec3 normal
) {

    /*
     * Rapid changes in the interpolated
     * normal indicate geometric or
     * surface detail.
     */
    vec3 normalDx =
        dFdx(
            normal
        );


    vec3 normalDy =
        dFdy(
            normal
        );


    float change =
        length(
            normalDx
        ) +
        length(
            normalDy
        );


    return clamp(
        change *
        uDetailStrength,
        0.0,
        1.0
    );
}


/*
 * ============================================================
 * MAIN
 * ============================================================
 */

void main() {

    vec2 surfaceUv =
        transformSurfaceUv(
            vUv
        );


    vec3 surfaceColor =
        getSurfaceColor(
            surfaceUv
        );


    float ao =
        getAmbientOcclusion(
            surfaceUv
        );


    vec3 emissive =
        getEmissiveColor(
            surfaceUv
        );


    vec3 surfaceNormal =
        getSurfaceNormal(
            surfaceUv
        );


    vec3 lightDirection =
        normalize(
            uLightDirection
        );


    float diffuse =
        max(
            dot(
                surfaceNormal,
                lightDirection
            ),
            0.0
        );


    /*
     * Keep the object relatively bright
     * and diagram-like rather than using
     * full conventional diffuse shading.
     */
    float diffuseContribution =
        diffuse *
        uLightIntensity *
        uShadingStrength;


    float ambient =
        uAmbientIntensity *
        ao;


    vec3 lighting =
        vec3(
            ambient
        ) +
        (
            uLightColor *
            diffuseContribution
        );


    /*
     * Retain a white-paper contribution
     * so low-light regions do not simply
     * become conventional dark shading.
     */
    lighting =
        mix(
            vec3(1.0),
            lighting,
            uShadingStrength
        );


    vec3 illustratedSurface =
        surfaceColor *
        lighting;


    /*
     * View-dependent outer/grazing edge.
     */
    float silhouette =
        getSilhouette(
            surfaceNormal
        );


    /*
     * Local normal discontinuities.
     */
    float detailEdge =
        getDetailEdge(
            surfaceNormal
        );


    float edge =
        max(
            silhouette,
            detailEdge
        );


    edge =
        clamp(
            edge,
            0.0,
            1.0
        );


    vec3 finalColor =
        mix(
            illustratedSurface,
            uLineColor,
            edge
        );


    finalColor +=
        emissive;


    gl_FragColor =
        vec4(
            finalColor,
            1.0
        );
}

`;

export default fragmentShader;
